import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from './Sidebar';
import TrialBanner from './TrialBanner';
import PaymentMethodBanner from './PaymentMethodBanner';
import ErrorBoundary from './ErrorBoundary';
import AskMantlePanel from './AskMantlePanel';
import { AiContextProvider } from '../contexts/AiContext';
import useAiPrefs from '../hooks/useAiPrefs';
import api from '../lib/api';

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Combined gate: plan must include AI AND the workspace owner hasn't
  // toggled "Ask Mantle" off in Settings → AI.
  const ai = useAiPrefs();
  const aiAllowed = ai.enabled('ask_mantle');
  // Field techs never get the floating AI button — their whole app is Chat
  // + Work + Timeclock and they don't need AI overlapping the send button.
  const isFieldTech = ['field_tech', 'crew', 'helper'].includes((user?.role || '').toLowerCase());

  // The mobile sidebar drawer opens Ask Mantle via a global custom event
  // so it doesn't have to prop-drill setAiOpen through Sidebar.
  useEffect(() => {
    const handler = () => setAiOpen(true);
    window.addEventListener('mantle:open-ai', handler);
    return () => window.removeEventListener('mantle:open-ai', handler);
  }, []);

  // Payment-required gate — dormant. The backend no longer flags new
  // workspaces with `pending_payment_setup` (billing is paused platform-
  // wide). We keep the redirect for any legacy record still carrying
  // the flag, so those users see the paused Choose Plan screen instead
  // of a broken app shell.
  useEffect(() => {
    if (!user) return;
    if (location.pathname.startsWith('/onboarding')) return;  // already there
    if (location.pathname.startsWith('/settings')) return;    // let them download data
    let cancel = false;
    api.get('/billing/subscription').then(({ data }) => {
      if (cancel) return;
      if (data?.pending_payment_setup && !data?.is_lifetime) {
        navigate('/onboarding/choose-plan', { replace: true });
      }
    }).catch(() => {/* silent — the backend guard still enforces server-side */});
    return () => { cancel = true; };
  }, [user, location.pathname, navigate]);

  return (
    <AiContextProvider>
    <div className="min-h-screen bg-[#121A25]" data-testid="app-shell">
      {/* Sidebar is hidden entirely for field techs — their app is just
          the topbar-Chat link on /timeclock or /my-workorder, plus this
          Chat page (which has its own Back button to return home). */}
      {!isFieldTech && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}
      {/* Main content: top padding on mobile pushes below the fixed
          topbar + iOS safe-area-inset-top. Desktop drops the padding
          because the desktop sidebar handles layout differently. See
          `.mobile-topbar-offset` in index.css for the actual math. */}
      <main
        className={`transition-all duration-300 min-h-screen ${isFieldTech ? '' : 'mobile-topbar-offset'} md:pt-0 ${
          isFieldTech ? '' : (collapsed ? 'md:ml-[72px]' : 'md:ml-[240px]')
        }`}
      >
        <PaymentMethodBanner user={user} />
        <TrialBanner />
        <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
          {/* Per-route error boundary — keying on pathname makes the boundary
              reset automatically when the user navigates to a different page,
              so a crash on /schedule doesn't keep the fallback on /workorders. */}
          <ErrorBoundary key={location.pathname} label={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>

      {/* Floating Ask Mantle launcher — hidden on:
          - Onboarding (user is locked out anyway)
          - Any chat/messaging surface (/messages, /chat, /work — collides
            with the composer's Send button)
          - Invoice/estimate editors (they have their own Ask Mantle button
            in the toolbar)
          - Field-tech workspaces (crew/helper/field_tech). Their whole app
            is Chat + Work + Timeclock — they don't need AI hovering over
            the send button.
          Also hidden for Starter/Core plans without the `ai` feature.
          The panel itself stays mounted everywhere AI is allowed so pages
          can open it via the `mantle:open-ai` event. */}
      {aiAllowed && !isFieldTech && (
        <>
          {!location.pathname.startsWith('/onboarding')
            && !location.pathname.startsWith('/chat')
            && !location.pathname.startsWith('/messages')
            && !location.pathname.startsWith('/work')
            && !location.pathname.startsWith('/my-workorder')
            && !location.pathname.startsWith('/timeclock')
            && !/^\/(invoices|estimates)\/[^/]+/.test(location.pathname) && (
            <button
              data-testid="ask-mantle-launcher"
              onClick={() => setAiOpen(true)}
              title="Ask Mantle"
              aria-label="Open Ask Mantle"
              className={`hidden md:flex fixed bottom-5 right-5 z-30 w-14 h-14 rounded-full bg-gradient-to-br from-[#B98A2C] to-[#CBA33B] text-[#121A25] shadow-2xl shadow-[#B98A2C]/40 hover:scale-110 active:scale-95 transition-all items-center justify-center group ${aiOpen ? 'opacity-0 pointer-events-none' : ''}`}
            >
              <Sparkles size={22} strokeWidth={2.4} />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[#121A25] animate-pulse" />
            </button>
          )}
          <AskMantlePanel open={aiOpen} onClose={() => setAiOpen(false)} />
        </>
      )}
    </div>
    </AiContextProvider>
  );
}
